import React, { useEffect, useState, useRef } from "react";
import DropMenu from "./DropMenu";
import { useNavigate, useLocation } from "react-router-dom";
import AttendanceButton from "../pages/attendance/AttendanceButton";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { CHATMESSAGEALL } from "../services/actions";
import {
  Typography,
  Badge,
  Avatar,
  Box,
  Stack,
  Tooltip,
  Paper,
  Divider,
} from "@mui/material";

import {
  BsPersonCircle,
  BsFillGearFill,
  BsBell,
  BsBellFill,
  BsEnvelope,
  BsList,
  BsChatText,
  BsSearch,
  BsCheckCircleFill,
  BsExclamationCircleFill,
  BsInfoCircleFill,
  BsChatDotsFill,
  BsCheckAll,
  BsClockHistory,
  BsArrowRepeat,
  BsCircle,
} from "react-icons/bs";
import { FaSignOutAlt } from "react-icons/fa";
import VoiceMicButton from "./VoiceMicButton";

// ── Toolbar icon button wrapper ───────────────────────────
const ToolIcon = ({ children, onClick, title, active = false, pulse = false }) => (
  <Tooltip title={title || ''} arrow placement="bottom">
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        width: 38,
        height: 38,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: active ? '#2563eb' : '#64748b',
        background: active
          ? 'rgba(37,99,235,0.08)'
          : 'transparent',
        border: active
          ? '1px solid rgba(37, 99, 235, 0.2)'
          : '1px solid transparent',
        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        animation: pulse ? 'bellPulse 2.4s ease infinite' : 'none',
        '&:hover': {
          color: '#0f172a',
          background: '#f1f5f9',
          border: '1px solid #e2e8f0',
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'scale(0.93)',
        },
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

// ── Main Toolbar Component ────────────────────────────────
export default function MyToolBar({
  user,
  onToggled,
  onLogout,
  appName,
  logo,
  logosm,
  isSubscribed = false,
  onSubScribe = () => { }
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const chatallState = useSelector((state) => state?.chatallState);
  const chatmessageallState = useSelector((state) => state?.chatmessageallState);

  const getPageTitle = (pathname) => {
    const map = {
      "/": "Dashboard",
      "/dashboard/": "Dashboard",
      "/clients/": "Clients",
      "/works/": "Projects & Works",
      "/services/": "Services",
      "/tasks/": "Tasks Management",
      "/my-tasks/": "My Tasks",
      "/invoices/": "Invoices",
      "/repository/": "Document Repository",
      "/user-management/": "Employees & Roles",
      "/attendance-report/": "Attendance Report",
      "/my-leaves/": "My Leaves",
      "/leave-approvals/": "Leave Approvals",
      "/working-time-settings/": "Time Settings",
      "/appraisal/": "Appraisal",
      "/analytics/": "Analytics",
      "/chat/": "Chat & Messages",
      "/messages/": "Chat & Messages",
      "/notify/": "Notifications",
      "/help/": "Help & Support",
      "/audit-logs/": "Audit Logs",
      "/route-map/": "Route Map",
      "/employee-route-map/": "Employee Route Map",
      "/work-admin-queue/": "Work Admin Queue",
      "/more-info/": "Details View",
      "/profile/": "User Profile",
      "/role/": "Role & Permissions",
      "/assign-role/": "Assign Multiple Roles to User"
    };
    if (map[pathname]) return map[pathname];
    const clean = pathname.replace(/\//g, "").replace(/-/g, " ");
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : "Dashboard";
  };
  const [messages, setMessages] = React.useState([]);
  const [mailOpen, setMailOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const [showMailPreview, setShowMailPreview] = useState(false);

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setIsSearching(true);
        try {
          const res = await axios.get(`${process.env.REACT_APP_URL}search/?q=${searchQuery}`, {
            headers: { Authorization: `Bearer ${user?.token}` }
          });
          setSearchResults(res.data.results || []);
          setShowSearchDropdown(true);
        } catch (error) {
          console.error("Search failed", error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, user?.token]);

  // ── Fetch initial unread message count ──────────────────
  useEffect(() => {
    if (user?.id) {
      dispatch({ type: `${CHATMESSAGEALL}_GET`, payload: "chat/unread_count/" });
    }
  }, [user?.id, dispatch]);

  // ── Avatar dropdown items ─────────────────────────────
  const items = [
    {
      icon: (
        <Avatar
          src={user?.avatar}
          alt={user?.first_name}
          sx={{
            cursor: "pointer", width: 20, height: 20, fontSize: "8px",
            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
          }}
        >
          <Typography fontSize={10} fontWeight={700} color="#ffffff">
            {user?.first_name?.slice(0, 2)?.toUpperCase()}
          </Typography>
        </Avatar>
      ),
      label: user?.first_name?.toUpperCase(),
      command: () => { },
    },
    {
      label: <Typography sx={{ px: 1, fontSize: 14 }}>Profile</Typography>,
      icon: <BsPersonCircle fontSize="1em" color="#2563eb" />,
      command: () => navigate("/profile/"),
    },
    {
      label: <Typography sx={{ px: 1, fontSize: 14 }}>Settings</Typography>,
      icon: <BsFillGearFill fontSize="1em" color="#2563eb" />,
      command: () => navigate("/settings/"),
    },
    {
      label: <Typography sx={{ px: 1, fontSize: 14 }}>Logout</Typography>,
      icon: <FaSignOutAlt fontSize="1em" color="#dc2626" />,
      command: () => onLogout?.(),
    },
  ];

  // ── Notification history states ────────────────────────
  const [showNotifyDropdown, setShowNotifyDropdown] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const [notifyDaysFilter, setNotifyDaysFilter] = useState(7); // Default 7 days
  const [notifyTab, setNotifyTab] = useState("all"); // "all" | "unread"
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // ── Helper to format time ago ─────────────────────────
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now - d) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // ── Helper for notification icon & color ──────────────
  const getNotifyIcon = (title = '', event = '') => {
    const t = (title + ' ' + event).toLowerCase();
    if (t.includes('rejected') || t.includes('blocked') || t.includes('failed')) {
      return { icon: <BsExclamationCircleFill size={15} color="#ef4444" />, bg: '#fef2f2' };
    }
    if (t.includes('approved') || t.includes('completed') || t.includes('success')) {
      return { icon: <BsCheckCircleFill size={15} color="#10b981" />, bg: '#f0fdf4' };
    }
    if (t.includes('chat') || t.includes('message')) {
      return { icon: <BsChatDotsFill size={15} color="#3b82f6" />, bg: '#eff6ff' };
    }
    if (t.includes('assigned') || t.includes('work') || t.includes('task')) {
      return { icon: <BsBellFill size={15} color="#8b5cf6" />, bg: '#f5f3ff' };
    }
    return { icon: <BsInfoCircleFill size={15} color="#6366f1" />, bg: '#eef2ff' };
  };

  // ── Helper to resolve API URLs & tokens cleanly ───────
  const getApiUrl = (endpoint = '') => {
    let base = (process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/').replace(/["']/g, '').trim();
    if (!base.endsWith('/')) base += '/';
    return `${base}${endpoint.replace(/^\//, '')}`;
  };

  const getAuthToken = () => {
    if (user?.token) return user.token;
    try {
      const stored = JSON.parse(localStorage.getItem("auth") || '{}');
      return stored?.token;
    } catch (_) {
      return null;
    }
  };

  // ── Fetch notifications from API ───────────────────────
  const fetchNotifications = async (days = notifyDaysFilter) => {
    const token = getAuthToken();
    if (!token) return;
    setLoadingNotifications(true);
    try {
      const url = getApiUrl(`notification/?days=${days}`);
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = res.data?.data || res.data;
      const results = raw?.results || (Array.isArray(raw) ? raw : (res.data?.results || []));
      const list = Array.isArray(results) ? results : [];
      setNotificationsList(list);
      const unread = list.filter((n) => !n.is_read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (user?.id || getAuthToken()) {
      fetchNotifications(notifyDaysFilter);
    }
  }, [user?.id, notifyDaysFilter]);

  const handleMarkAllRead = async () => {
    const token = getAuthToken();
    try {
      await axios.post(
        getApiUrl('notification/mark-all-read/'),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationsList((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications read", err);
    }
  };

  const handleToggleRead = async (e, item) => {
    e.stopPropagation();
    const token = getAuthToken();
    const newStatus = !item.is_read;
    try {
      await axios.patch(
        getApiUrl(`notification/${item.id}/`),
        { is_read: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationsList((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, is_read: newStatus } : n))
      );
      setUnreadCount((c) => (newStatus ? Math.max(0, c - 1) : c + 1));
    } catch (err) {
      console.error("Failed to toggle notification read status", err);
    }
  };

  const handleNotificationClick = async (item) => {
    const token = getAuthToken();
    if (!item.is_read) {
      try {
        await axios.patch(
          getApiUrl(`notification/${item.id}/`),
          { is_read: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotificationsList((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (err) {
        console.error("Failed to mark notification read", err);
      }
    }
    setShowNotifyDropdown(false);

    // 1. If explicit URL is provided in the notification
    if (item.url && typeof item.url === 'string' && item.url.trim()) {
      let target = item.url.trim().replace(/^(\/#|#)/, '');
      if (!target.startsWith('/')) target = '/' + target;
      navigate(target);
      return;
    }

    // 2. Intelligent parsing from title and message for existing notifications
    const fullText = `${item.title || ''} ${item.message || ''} ${item.event || ''}`;
    const lowerText = fullText.toLowerCase();

    let extractedWorkName = null;
    const workNameQuoteMatch = fullText.match(/(?:The work|Work|in Work)\s*['"]([^'"]+)['"]/i);
    if (workNameQuoteMatch && workNameQuoteMatch[1]) {
      extractedWorkName = workNameQuoteMatch[1].trim();
    } else {
      const prefixMatch = item.title?.match(/^(?:Task Update|\[Task Blocked\]|\[On Review\]|\[Task Rejected\]|\[Chat\]|Work Update):\s*([^-\n]+?)(?:\s*-\s*|$)/i);
      if (prefixMatch && prefixMatch[1]) {
        extractedWorkName = prefixMatch[1].trim();
      }
    }

    if (lowerText.includes('chat') || lowerText.includes('message')) {
      navigate('/messages/');
    } else if (lowerText.includes('task') || lowerText.includes('rejected') || lowerText.includes('blocked') || lowerText.includes('review') || lowerText.includes('assigned')) {
      if (extractedWorkName) {
        navigate(`/tasks/?work_name=${encodeURIComponent(extractedWorkName)}&back=/works/`);
      } else {
        navigate('/tasks/');
      }
    } else if (lowerText.includes('work') || lowerText.includes('project')) {
      if (extractedWorkName) {
        navigate(`/tasks/?work_name=${encodeURIComponent(extractedWorkName)}&back=/works/`);
      } else {
        navigate('/works/');
      }
    } else {
      navigate('/tasks/');
    }
  };

  // ── Chat message unread count ──────────────────────────
  useEffect(() => {
    const data = chatmessageallState?.data;
    if (!data || !data.per_user) return;
    setUnreadMsgCount(data.total || 0);
    // Build recent messages list from per_user
    setRecentMessages(data.per_user || []);
  }, [chatmessageallState?.data]);

  // ── SSE: real-time chat badge bump & notification sync ─
  useEffect(() => {
    if (!user?.id) return;
    const url = getApiUrl(`sse/stream/${user.id}/`);
    const es = new EventSource(url);
    es.onmessage = (e) => {
      try {
        const raw = JSON.parse(e.data);
        const msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (msg.event === 'chat' && msg.message_type === 'chat') {
          setUnreadMsgCount((c) => c + 1);
          setRecentMessages((prev) => [
            { sender_id: msg.sender, sender__first_name: msg.sender_name, count: 1 },
            ...prev.filter((m) => m.sender_id !== msg.sender)
          ]);
        }
        if (msg.event === 'notification' || msg.event === 'tagged_work' || msg.event === 'tagged_task' || msg.event === 'info' || msg.event === 'warning') {
          setUnreadCount((c) => c + 1);
          fetchNotifications(notifyDaysFilter);
        }
      } catch (_) { }
    };
    return () => es.close();
  }, [user?.id, notifyDaysFilter]);

  const handleBellClick = () => {
    onSubScribe(user?.id);
    setShowNotifyDropdown((v) => !v);
    setShowMailPreview(false);
    if (!showNotifyDropdown) {
      fetchNotifications(notifyDaysFilter);
    }
  };

  // ── Render ────────────────────────────────────────────
  return (
    <Box
      className="ps-toolbar glass-toolbar"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        top: 0,
        zIndex: 1000,
        px: 3,
        height: 64,
        borderRadius: 0,
      }}
    >
      {/* ── Left Side: Hamburger & Dynamic Navigation Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Toggle Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '10px',
            cursor: "pointer",
            color: '#475569',
            border: '1px solid transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: '#f1f5f9',
              color: '#0f172a',
              border: '1px solid #e2e8f0',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          onClick={() => document.getElementById("side-bar").classList.toggle("show-menu")}
        >
          <BsList size={22} />
        </Box>

        {/* Dynamic Nav Label / Greeting */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 700,
              color: '#0f172a',
              fontFamily: 'Poppins, sans-serif',
              letterSpacing: '-0.01em',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {getPageTitle(location.pathname)}
          </Typography>
        </Box>
      </Box>

      {/* ── Center Side: Premium SaaS Search Input ── */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: 'center',
          flex: 1,
          minWidth: 160,
          maxWidth: 340,
          mx: { xs: 1.5, md: 3 }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            height: '38px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            px: 1.5,
            gap: 1.2,
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            '&:focus-within': {
              background: '#ffffff',
              borderColor: '#3b82f6',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b', flexShrink: 0 }}>
            <BsSearch size={14} />
          </Box>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search projects, tasks, files..."
            style={{
              flex: 1,
              width: '100%',
              minWidth: '110px',
              height: '100%',
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              color: '#0f172a',
              padding: 0,
              margin: 0
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchQuery.length >= 2) setShowSearchDropdown(true);
            }}
            onBlur={() => {
              setTimeout(() => setShowSearchDropdown(false), 200); // delay to allow clicks
            }}
          />
          <VoiceMicButton
            initialText={searchQuery}
            onTranscript={(text) => {
              setSearchQuery(text);
              if (text.length >= 2) setShowSearchDropdown(true);
            }}
            size={14}
            style={{ p: 0.4 }}
          />
          <Box
            sx={{
              px: '6px',
              py: '2px',
              borderRadius: '6px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              fontSize: '10px',
              fontWeight: 700,
              color: '#64748b',
              letterSpacing: '0.05em',
              pointerEvents: 'none',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              flexShrink: 0
            }}
          >
            ⌘K
          </Box>
        </Box>

        {/* Dropdown for Search Results */}
        {showSearchDropdown && (
          <Box
            sx={{
              position: 'absolute',
              top: '55px',
              width: '100%',
              maxWidth: 320,
              maxHeight: 300,
              overflowY: 'auto',
              background: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              zIndex: 9999,
              border: '1px solid #e2e8f0',
              p: 1
            }}
          >
            {isSearching ? (
              <Typography variant="body2" p={1} color="textSecondary">Searching...</Typography>
            ) : searchResults.length > 0 ? (
              searchResults.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    '&:hover': { background: '#f8fafc' }
                  }}
                  onClick={() => {
                    setShowSearchDropdown(false);
                    setSearchQuery("");
                    if (item.type === 'Work') navigate(`/works/`);
                    else if (item.type === 'Client') navigate(`/clients/`);
                    else if (item.type === 'Document') navigate(`/repository/`);
                    else if (item.type === 'Task') navigate(`/tasks/`);
                    else if (item.type === 'Service') navigate(`/services/`);
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>{item.type}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#1e293b">{item.title}</Typography>
                  <Typography variant="caption" color="textSecondary">{item.subtitle}</Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" p={1} color="textSecondary">No results found.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* ── Right Side: Action Icons & User Dropdown ── */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Attendance Check In/Out Button */}
        <Box sx={{ mr: 0.5, display: { xs: 'none', sm: 'block' } }}>
          <AttendanceButton />
        </Box>

        {/* Chat icon */}
        <ToolIcon title="Messages" onClick={() => navigate("/messages/")}>
          <BsChatText size={17} />
        </ToolIcon>

        {/* Bell / Notifications Dropdown */}
        <Box sx={{ position: 'relative' }}>
          <ToolIcon
            title={unreadCount > 0 ? `${unreadCount} new notifications` : "Notifications & History"}
            onClick={handleBellClick}
            active={showNotifyDropdown || isSubscribed}
            pulse={unreadCount > 0}
          >
            <Badge
              badgeContent={unreadCount > 0 ? unreadCount : null}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '9px',
                  minWidth: '16px',
                  height: '16px',
                  padding: '0 4px',
                }
              }}
            >
              <BsBell
                size={17}
                color={unreadCount > 0 ? '#ef4444' : isSubscribed ? '#22c55e' : '#64748b'}
              />
            </Badge>
          </ToolIcon>

          {/* Modern Notifications Dropdown Window */}
          {showNotifyDropdown && (
            <Paper
              elevation={12}
              sx={{
                position: 'absolute',
                top: 48,
                right: { xs: -60, sm: 0 },
                width: { xs: 330, sm: 400 },
                maxHeight: 520,
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                zIndex: 9999,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 12px 36px -4px rgba(15, 23, 42, 0.16)',
                background: '#ffffff'
              }}
            >
              {/* Dropdown Header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid #f1f5f9',
                  background: 'linear-gradient(to right, #f8fafc, #ffffff)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={700} fontSize={14} color="#0f172a">
                    Notifications & Push Alerts
                  </Typography>
                  {unreadCount > 0 && (
                    <Box
                      sx={{
                        bgcolor: '#fee2e2',
                        color: '#dc2626',
                        fontSize: 11,
                        fontWeight: 700,
                        px: 1,
                        py: 0.2,
                        borderRadius: '999px'
                      }}
                    >
                      {unreadCount} unread
                    </Box>
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <Tooltip title="Refresh & Re-check Notifications">
                    <Box
                      onClick={() => fetchNotifications(notifyDaysFilter)}
                      sx={{
                        cursor: 'pointer',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.5,
                        borderRadius: '6px',
                        '&:hover': { color: '#2563eb', bgcolor: '#f1f5f9' }
                      }}
                    >
                      <BsArrowRepeat size={16} />
                    </Box>
                  </Tooltip>

                  {unreadCount > 0 && (
                    <Typography
                      fontSize={12}
                      color="#2563eb"
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={handleMarkAllRead}
                    >
                      <BsCheckAll size={16} /> Mark all read
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Range Filter & Tabs */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#f8fafc'
                }}
              >
                <Box display="flex" gap={0.8}>
                  <Box
                    onClick={() => setNotifyTab("all")}
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: notifyTab === "all" ? '#2563eb' : 'transparent',
                      color: notifyTab === "all" ? '#ffffff' : '#64748b',
                      transition: 'all 0.15s'
                    }}
                  >
                    All ({notificationsList.length})
                  </Box>
                  <Box
                    onClick={() => setNotifyTab("unread")}
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: notifyTab === "unread" ? '#2563eb' : 'transparent',
                      color: notifyTab === "unread" ? '#ffffff' : '#64748b',
                      transition: 'all 0.15s'
                    }}
                  >
                    Unread ({unreadCount})
                  </Box>
                  <Box
                    onClick={() => setNotifyTab("read")}
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: notifyTab === "read" ? '#2563eb' : 'transparent',
                      color: notifyTab === "read" ? '#ffffff' : '#64748b',
                      transition: 'all 0.15s'
                    }}
                  >
                    Read ({Math.max(0, notificationsList.length - unreadCount)})
                  </Box>
                </Box>

                {/* Days range selector */}
                <select
                  value={notifyDaysFilter}
                  onChange={(e) => {
                    const d = Number(e.target.value);
                    setNotifyDaysFilter(d);
                    fetchNotifications(d);
                  }}
                  style={{
                    fontSize: 11,
                    padding: '3px 8px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    outline: 'none',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  <option value={1}>Today</option>
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                </select>
              </Box>

              {/* Notification List Scrollable Body */}
              <Box sx={{ overflowY: 'auto', flex: 1, maxHeight: 360 }}>
                {loadingNotifications ? (
                  <Typography fontSize={13} color="#94a3b8" sx={{ p: 3, textAlign: 'center' }}>
                    Loading notifications...
                  </Typography>
                ) : notificationsList.filter(n => notifyTab === "unread" ? !n.is_read : notifyTab === "read" ? n.is_read : true).length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <BsClockHistory size={28} color="#cbd5e1" style={{ marginBottom: 8 }} />
                    <Typography fontSize={13} fontWeight={600} color="#64748b">
                      No notifications found
                    </Typography>
                    <Typography fontSize={11} color="#94a3b8" sx={{ mt: 0.5 }}>
                      {notifyTab === "unread" ? "No unread notifications right now." : "Past push messages for the selected period will appear here."}
                    </Typography>
                  </Box>
                ) : (
                  notificationsList
                    .filter(n => notifyTab === "unread" ? !n.is_read : notifyTab === "read" ? n.is_read : true)
                    .map((n) => {
                      const iconMeta = getNotifyIcon(n.title, n.event);
                      return (
                        <Box
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          sx={{
                            px: 2,
                            py: 1.4,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            cursor: 'pointer',
                            bgcolor: !n.is_read ? 'rgba(37, 99, 235, 0.05)' : '#ffffff',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.15s',
                            '&:hover': {
                              bgcolor: !n.is_read ? 'rgba(37, 99, 235, 0.09)' : '#f8fafc'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '8px',
                              bgcolor: iconMeta.bg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              mt: 0.2
                            }}
                          >
                            {iconMeta.icon}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography
                                fontSize={12.5}
                                fontWeight={!n.is_read ? 700 : 600}
                                color={!n.is_read ? '#0f172a' : '#334155'}
                                noWrap
                              >
                                {n.title}
                              </Typography>
                              <Typography fontSize={10.5} color="#94a3b8" flexShrink={0} ml={1}>
                                {timeAgo(n.created_at)}
                              </Typography>
                            </Box>
                            <Typography
                              fontSize={11.5}
                              color="#64748b"
                              sx={{
                                mt: 0.3,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {n.message}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.8} sx={{ ml: 0.5, mt: 0.5 }}>
                            <Tooltip title={n.is_read ? "Mark as unread (Review later)" : "Mark as read"}>
                              <Box
                                onClick={(e) => handleToggleRead(e, n)}
                                sx={{
                                  cursor: 'pointer',
                                  color: n.is_read ? '#94a3b8' : '#2563eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 0.4,
                                  borderRadius: '4px',
                                  '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' }
                                }}
                              >
                                {n.is_read ? <BsCircle size={12} /> : <BsCheckCircleFill size={13} />}
                              </Box>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })
                )}
              </Box>

              {/* Dropdown Footer */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderTop: '1px solid #f1f5f9'
                }}
              >
                <Typography fontSize={11} color="#64748b">
                  Showing push messages from past {notifyDaysFilter} day{notifyDaysFilter > 1 ? 's' : ''}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Envelope with unread badge + preview dropdown */}
        <Box sx={{ position: 'relative' }}>
          <ToolIcon
            title="Messages"
            active={unreadMsgCount > 0}
            onClick={() => {
              setShowMailPreview((v) => !v);
            }}
          >
            <Badge
              badgeContent={unreadMsgCount > 0 ? unreadMsgCount : null}
              color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '9px', minWidth: '16px', height: '16px', padding: '0 4px' } }}
            >
              <BsEnvelope size={17} />
            </Badge>
          </ToolIcon>

          {/* Dropdown preview */}
          {showMailPreview && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute', top: 46, right: 0,
                width: 280, borderRadius: '12px',
                border: '1px solid #e2e8f0', zIndex: 9999, overflow: 'hidden'
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight={700} fontSize={13} color="#0f172a">Messages</Typography>
                <Typography
                  fontSize={12} color="#2563eb" sx={{ cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => { navigate('/messages/'); setShowMailPreview(false); setUnreadMsgCount(0); }}
                >
                  View all
                </Typography>
              </Box>
              {recentMessages.length === 0 ? (
                <Typography fontSize={13} color="#94a3b8" sx={{ p: 2, textAlign: 'center' }}>No new messages</Typography>
              ) : (
                recentMessages.slice(0, 3).map((m, i) => (
                  <Box
                    key={i}
                    onClick={() => { navigate('/messages/', { state: { selectedUser: Number(m.sender_id) } }); setShowMailPreview(false); setUnreadMsgCount(0); }}
                    sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { background: '#f8fafc' }, borderBottom: '1px solid #f8fafc' }}
                  >
                    <Avatar sx={{ width: 32, height: 32, background: '#2563eb', fontSize: 12, fontWeight: 700 }}>
                      {m.sender__first_name?.slice(0, 2)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={13} fontWeight={600} color="#1e293b">{m.sender__first_name}</Typography>
                      <Typography fontSize={11.5} color="#94a3b8">{m.count} unread message{m.count > 1 ? 's' : ''}</Typography>
                    </Box>
                  </Box>
                ))
              )}
              <Box
                onClick={() => { navigate('/messages/'); setShowMailPreview(false); setUnreadMsgCount(0); }}
                sx={{ px: 2, py: 1.2, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', '&:hover': { background: '#f1f5f9' } }}
              >
                <Typography fontSize={12} color="#2563eb" fontWeight={600}>Open Messages →</Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Vertical Divider */}
        <Box sx={{
          width: '1px',
          height: '20px',
          background: '#e2e8f0',
          mx: 0.5,
        }} />

        {/* User profile layout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* User name text */}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 13.5,
              color: '#334155',
              display: { xs: "none", sm: "block" },
              letterSpacing: '0.01em',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            {user?.first_name}
          </Typography>

          {/* Premium Avatar Ring Dropdown */}
          <Box sx={{ position: "relative" }}>
            <DropMenu
              model={items}
              popup
              icon={
                <Box
                  sx={{
                    position: 'relative',
                    p: '2px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.06)',
                      boxShadow: '0 0 14px rgba(37,99,235,.20)',
                    },
                  }}
                >
                  <Avatar
                    src={user?.avatar}
                    alt={user?.first_name}
                    sx={{
                      width: 32,
                      height: 32,
                      background: '#2563eb',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    <Typography fontSize={12} fontWeight={700} color="#ffffff">
                      {user?.first_name?.slice(0, 2)?.toUpperCase()}
                    </Typography>
                  </Avatar>
                  {/* Status Indicator Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 1,
                      right: 1,
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: '#22c55e',
                      border: '2px solid #ffffff',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
                    }}
                  />
                </Box>
              }
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
